import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  BookmarkIcon,
  CheckIcon,
  MicIcon,
  PaperclipIcon,
  PauseIcon,
  PlayIcon,
  SmileIcon,
  SquareIcon,
  StickyNoteIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { EmojiPicker } from '@/components/organisms/EmojiPicker/EmojiPicker';
import { MODAL_CANNED_RESPONSES } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import { SELECTED_SURFACE } from '@/components/ui/constants';
import { Kbd } from '@/components/ui/kbd';
import { Spinner } from '@/components/ui/spinner';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import UploadCard from '@/modules/humanChat/components/Conversation/MessageArea/FileUploadChatBox/UploadCard/UploadCard';
import { useConversationDetail } from '@/modules/humanChat/hooks/useConversations';
import { useScheduledMessages } from '@/modules/humanChat/hooks/useScheduledMessages';
import { useTemplateMessage } from '@/modules/humanChat/hooks/useTemplateMessage';
import { pushModal } from '@/redux/modals/actions';
import { selectSessionId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { formatSecondsToMmSs } from '@/util/dateUtils';
import { preventClickThrough } from '@/util/util';

import QuickResponses from './QuickResponses';
import SavedResponsesPanel from './SavedResponsesPanel';
import { CHANNELS_WITH_VOICE_MEMOS } from '../ChatBox/constants';
import { type ChatBoxController } from '../ChatBox/useChatBox';
import ScheduledMessagesList from '../ScheduledMessages/ScheduledMessagesList';
import ScheduleSendButton from '../ScheduledMessages/ScheduleSendButton';

const ICON_SIZE = 20;
export const ACTIVE_TOOLBAR_BUTTON = `border ${SELECTED_SURFACE} hover:bg-selected-soft hover:text-foreground [&_svg]:text-selected`;

type FileUploadChatBoxViewProps = { chatBox: ChatBoxController; onAddNote: () => void };

export const FileUploadChatBoxView = ({ chatBox, onAddNote }: FileUploadChatBoxViewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const { conversation } = useConversationDetail({ deskId, sessionId });
  const [showPanel, setShowPanel] = useState(false);
  const [showScheduledList, setShowScheduledList] = useState(false);
  const [emojiPicker, setEmojiPicker] = useState(false);
  const savedSelectionRef = useRef<Range | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  const {
    files,
    isClosed,
    isActive,
    inputRef,
    isLoading,
    filteredOptions,
    isButtonDisabled,
    showQuickResponses,
    handleClick,
    handleChange,
    changeInputTo,
    selectQuickResponse,
    handleKeyPress,
    handleKeyDown,
    handleOnRemoveFile,
    handlePaste,
    getRootProps,
    getInputProps,
    open,
    isDragActive,
    audioUrl,
    handleStartRecording,
    isRecording,
    recordingBlob,
    recordingTime,
    handleDeleteRecording,
    handleStopRecording,
    isTranscoding,
    totalRecordingTime,
    isChannelSupported,
    containerRef,
    isPlaying,
    currentTime,
    duration,
    playPause,
  } = chatBox;
  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filesToRender = useMemo(
    () =>
      files.map((file, idx) => {
        const fileName = file.name;
        if (file.type?.includes('image')) {
          return (
            <div
              className={cn(
                'group relative mx-0 mr-2 mb-2 rounded hover:brightness-[0.8] hover:contrast-125',
                isLoading && 'pointer-events-none'
              )}
              key={file.path}
            >
              <div className="h-12 w-12 overflow-hidden rounded shadow-md [&_img]:h-12 [&_img]:w-12 [&_img]:object-cover">
                <img
                  src={file.preview}
                  alt={fileName}
                  onLoad={() => {
                    URL.revokeObjectURL(file.preview);
                  }}
                  onDragStart={(e) => preventClickThrough(e)}
                />

                {!isLoading && (
                  <XIcon
                    onClick={(e) => handleOnRemoveFile(e, idx, file.fileId)}
                    onKeyDown={(e) => handleOnRemoveFile(e, idx, file.fileId)}
                    className="absolute -top-2 -right-2 hidden cursor-pointer rounded-full bg-muted-foreground p-0.5 text-background group-hover:inline hover:bg-muted-foreground"
                    size={16}
                  />
                )}
              </div>
            </div>
          );
        }
        return (
          <UploadCard
            fileName={file.name?.split('.')[0]}
            fileType={file.name?.split('.').pop()}
            handleClose={(e) => handleOnRemoveFile(e, idx, file.fileId)}
            key={file.path}
          />
        );
      }),
    [files, handleOnRemoveFile, isLoading]
  );

  const handleEmojiButtonClick = useCallback(() => {
    if (emojiPicker) {
      setEmojiPicker(false);
      return;
    }
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }
    setEmojiPicker(true);
  }, [emojiPicker]);

  const handleEmojiClick = useCallback(
    ({ emoji }) => {
      const input = inputRef.current;
      const sel = window.getSelection();
      if (!input || !sel) {
        return;
      }

      input.focus();

      // Restore saved position or create one at end
      let range: Range;
      if (savedSelectionRef.current) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
        range = savedSelectionRef.current;
      } else if (sel.rangeCount > 0) {
        range = sel.getRangeAt(0);
      } else {
        range = document.createRange();
        range.selectNodeContents(input);
        range.collapse(false);
        sel.addRange(range);
      }

      const textNode = document.createTextNode(emoji);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);

      handleChange();
      savedSelectionRef.current = null;
      setEmojiPicker(false);
    },
    [handleChange, inputRef]
  );

  const shouldDisableButtons = !isActive || !isChannelSupported || isClosed;

  const { handleClick: onBrowseTemplates } = useTemplateMessage();
  const { isEnabled: isSchedulingEnabled, pendingCount } = useScheduledMessages(deskId, sessionId);

  // Close the overlay when the last pending message is cancelled or fires —
  // the list self-hides, but the invisible backdrop would otherwise stay
  // mounted and swallow the next click.
  useEffect(() => {
    if (pendingCount === 0) {
      setShowScheduledList(false);
    }
  }, [pendingCount]);

  const timer = useMemo(() => {
    if (isRecording) {
      return formatSecondsToMmSs(recordingTime);
    }
    // During playback, show currentTime; otherwise show total duration
    const time = isPlaying || currentTime > 0 ? currentTime : duration || totalRecordingTime;
    return formatSecondsToMmSs(time);
  }, [isRecording, recordingTime, isPlaying, currentTime, duration, totalRecordingTime]);

  const channelSupportsVoiceMemos = CHANNELS_WITH_VOICE_MEMOS.includes(conversation?.channel);
  const isRecordingActive = !!(audioUrl || isRecording || recordingBlob || isTranscoding);
  return (
    <>
      {emojiPicker && (
        <div className="absolute bottom-full left-0 z-10 mb-2 h-50 w-full">
          <EmojiPicker
            onClickAway={(e) => {
              if (emojiButtonRef.current?.contains(e.target as Node)) {
                return;
              }
              setEmojiPicker(false);
            }}
            onEmojiClick={handleEmojiClick}
            width="100%"
            height={200}
          />
        </div>
      )}

      <div
        {...getRootProps({
          className: cn(
            'h-full [&>div]:flex [&>div]:flex-col [&>div:first-child]:h-full',
            isDragActive &&
              'flex h-full items-center justify-center rounded border-border bg-muted/50 text-primary outline-1 outline-border outline-dashed'
          ),
        })}
      >
        <div className={cn(isDragActive && 'invisible opacity-0')}>
          <div
            ref={inputRef}
            className="max-h-70 flex-1 overflow-hidden overflow-y-auto border-0 wrap-anywhere focus:border-0 focus:outline-0 [&[contenteditable]:empty::before]:text-muted-foreground [&[contenteditable]:empty::before]:content-[attr(data-placeholder)]"
            aria-label="input"
            role="textbox"
            contentEditable={!shouldDisableButtons}
            tabIndex={0}
            onKeyUp={handleKeyPress}
            onKeyDown={handleKeyDown}
            onInput={handleChange}
            onPaste={handlePaste}
            data-testid="file-upload-chat-box"
            data-placeholder={t('chatBox.placeholder')}
          />
          {files && !isRecordingActive && (
            <div className="mt-6 flex flex-wrap">{filesToRender}</div>
          )}
          <div
            className={cn(
              'chatBoxFooter flex w-full max-w-full items-center [&_input]:w-full',
              isDragActive && 'justify-center'
            )}
          >
            {/* Toolbar — hidden while recording/playing */}
            {!isRecordingActive && (
              <div className="flex self-end">
                <InfoTooltip content={t('chatBox.quick_responses')} showArrow>
                  <Button
                    type="button"
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={() => {
                      setShowScheduledList(false);
                      setShowPanel((prev) => !prev);
                    }}
                    aria-label={t('chatBox.quick_responses')}
                    disabled={shouldDisableButtons}
                    className={cn(showPanel && ACTIVE_TOOLBAR_BUTTON)}
                  >
                    <BookmarkIcon size={ICON_SIZE} />
                  </Button>
                </InfoTooltip>

                <InfoTooltip content={t('notes.add_note')} showArrow>
                  <Button
                    type="button"
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={() => {
                      trackEvent(EventName.OpenNoteComposer, { session_id: sessionId });
                      onAddNote();
                    }}
                    aria-label={t('notes.add_note')}
                    disabled={shouldDisableButtons}
                  >
                    <StickyNoteIcon size={ICON_SIZE} />
                  </Button>
                </InfoTooltip>

                <InfoTooltip content={t('chatBox.attach_file')} showArrow>
                  <Button
                    type="button"
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={open}
                    aria-label={t('chatBox.attach_file')}
                    disabled={shouldDisableButtons}
                  >
                    <PaperclipIcon size={ICON_SIZE} />
                  </Button>
                </InfoTooltip>

                <InfoTooltip content={t('emojiPicker.emoji_panel')} showArrow>
                  <Button
                    ref={emojiButtonRef}
                    type="button"
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={handleEmojiButtonClick}
                    aria-label={t('emojiPicker.emoji_panel')}
                    disabled={shouldDisableButtons}
                    className={cn(emojiPicker && ACTIVE_TOOLBAR_BUTTON)}
                  >
                    <SmileIcon size={ICON_SIZE} />
                  </Button>
                </InfoTooltip>
              </div>
            )}

            {/* Recording / playback controls */}
            {isRecordingActive && (
              <div className="flex max-w-sm flex-1 items-center gap-2">
                {/* Pill: live indicator, waveform, timer, primary action */}
                <div className="flex flex-1 items-center gap-4 rounded-xl border border-border bg-background py-2 pr-2 pl-4">
                  {isRecording ? (
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
                    </span>
                  ) : (
                    <CheckIcon className="size-4 shrink-0 text-muted-foreground" />
                  )}

                  <div className="relative h-8 min-w-0 flex-1">
                    <div ref={containerRef} className="absolute inset-0 overflow-hidden" />
                  </div>

                  {isTranscoding && <Spinner aria-label={t('common.loading')} />}

                  <span className="shrink-0 text-sm font-medium text-foreground tabular-nums">
                    {timer}
                  </span>

                  {isRecording && (
                    <InfoTooltip content={t('chatbox.stop')} showArrow>
                      <Button
                        type="button"
                        size="icon-sm"
                        onClick={handleStopRecording}
                        aria-label={t('chatbox.stop')}
                      >
                        <SquareIcon className="fill-current" />
                      </Button>
                    </InfoTooltip>
                  )}

                  {audioUrl && (
                    <InfoTooltip content={isPlaying ? t('chat.pause') : t('chat.play')} showArrow>
                      <Button
                        type="button"
                        size="icon-sm"
                        onClick={playPause}
                        aria-label={isPlaying ? t('chat.pause') : t('chat.play')}
                      >
                        {isPlaying ? <PauseIcon /> : <PlayIcon />}
                      </Button>
                    </InfoTooltip>
                  )}
                </div>

                {/* Trash sits outside the pill */}
                <div className={cn(!recordingBlob && 'pointer-events-none invisible')}>
                  <InfoTooltip content={t('chatbox.delete_recording')} showArrow>
                    <Button
                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      onClick={handleDeleteRecording}
                      aria-label={t('chatbox.delete_recording')}
                    >
                      <Trash2Icon />
                    </Button>
                  </InfoTooltip>
                </div>
              </div>
            )}

            <input {...getInputProps()} />
            <div className="ml-auto flex items-center gap-2 self-end">
              {channelSupportsVoiceMemos && (
                <div className={cn(isRecordingActive && 'pointer-events-none invisible')}>
                  <InfoTooltip content={t('chatbox.microphone')} showArrow>
                    <Button
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      onClick={handleStartRecording}
                      aria-label={t('chatbox.microphone')}
                      disabled={shouldDisableButtons}
                    >
                      <MicIcon size={18} />
                    </Button>
                  </InfoTooltip>
                </div>
              )}
              <div className={cn(isRecordingActive && 'pointer-events-none invisible')}>
                <ScheduleSendButton
                  onBrowseTemplates={() => onBrowseTemplates('schedule')}
                  onToggleList={() => {
                    setShowPanel(false);
                    setShowScheduledList((prev) => !prev);
                  }}
                  disabled={shouldDisableButtons}
                />
              </div>
              <InfoTooltip
                content={
                  <span className="flex items-center gap-1.5">
                    {t('chatBox.send')} <Kbd>↵</Kbd>
                  </span>
                }
                size="auto"
              >
                {/* Wrapper keeps the tooltip reachable while the button is disabled. */}
                <span className="inline-flex">
                  <Button
                    size="sm"
                    isLoading={isLoading}
                    disabled={isButtonDisabled}
                    onClick={handleClick}
                  >
                    {t('chatBox.send')}
                  </Button>
                </span>
              </InfoTooltip>
            </div>
          </div>
        </div>
        {isDragActive && (
          <div className="absolute left-1/2 -translate-x-1/2">{t('chatBox.drop_files')}</div>
        )}
      </div>

      {showPanel && (
        <>
          <div
            aria-hidden="true"
            className="fixed inset-0 z-[9]"
            onClick={() => setShowPanel(false)}
          />
          <SavedResponsesPanel
            onSelect={(text) => {
              changeInputTo(text);
              setShowPanel(false);
            }}
            onAdd={() => dispatch(pushModal(MODAL_CANNED_RESPONSES))}
            onClose={() => setShowPanel(false)}
            onBrowseTemplates={
              isSchedulingEnabled
                ? () => {
                    setShowPanel(false);
                    onBrowseTemplates('schedule');
                  }
                : undefined
            }
          />
        </>
      )}

      {showScheduledList && (
        <>
          <div
            aria-hidden="true"
            className="fixed inset-0 z-[9]"
            onClick={() => setShowScheduledList(false)}
          />
          <ScheduledMessagesList variant="overlay" />
        </>
      )}

      {/* Hidden while the saved-responses panel is open — both render in the same spot. */}
      {showQuickResponses && !showPanel && !showScheduledList && (
        <QuickResponses handleClick={selectQuickResponse} filteredOptions={filteredOptions} />
      )}
    </>
  );
};
